import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Printer, Radio, Plus, SquarePen, Trash2, Copy, Check, RefreshCw, KeyRound,
  ListChecks, RotateCcw,
} from 'lucide-react';
import Layout from '../../components/Layout';
import FieldHint from '../../components/FieldHint';
import {
  printerService, PrinterProfile, PrinterType, PrintAgent, RegisterPrintAgentResult,
  PrintJob, PrintJobStatus,
} from '../../services/printerService';
import './PrintDevicesPage.css';

/**
 * Printing Devices settings — two sections:
 *   1. Printer profiles: named printers (thermal / A4) bills are routed to.
 *   2. Print agents: site-installed bridges; registering shows the API key once.
 */

interface ProfileForm {
  name: string;
  printerName: string;
  printerType: PrinterType;
  paperWidthMm: number;
  isDefault: boolean;
  kickCashDrawer: boolean;
  isActive: boolean;
}

const emptyProfileForm: ProfileForm = {
  name: '',
  printerName: '',
  printerType: PrinterType.Thermal,
  paperWidthMm: 80,
  isDefault: false,
  kickCashDrawer: false,
  isActive: true,
};

const PrintDevicesPage: React.FC = () => {
  const { t } = useTranslation();

  const [profiles, setProfiles] = useState<PrinterProfile[]>([]);
  const [agents, setAgents] = useState<PrintAgent[]>([]);
  const [jobs, setJobs] = useState<PrintJob[]>([]);
  const [retryingJobId, setRetryingJobId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Profile modal state — null means closed; id 0 means "create".
  const [profileModal, setProfileModal] = useState<{ id: number; form: ProfileForm } | null>(null);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState('');

  // Agent modal state
  const [agentModalOpen, setAgentModalOpen] = useState(false);
  const [agentName, setAgentName] = useState('');
  const [agentSaving, setAgentSaving] = useState(false);
  const [agentError, setAgentError] = useState('');
  const [issuedKey, setIssuedKey] = useState<RegisterPrintAgentResult | null>(null);
  const [keyCopied, setKeyCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [profilesRes, agentsRes, jobsRes] = await Promise.all([
        printerService.getProfiles(),
        printerService.getAgents(),
        printerService.getJobs(),
      ]);
      setProfiles(profilesRes.data.data ?? []);
      setAgents(agentsRes.data.data ?? []);
      setJobs(jobsRes.data.data ?? []);
    } catch {
      setError(t('printDevices.loadFailed') || 'Failed to load printing devices');
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const flashSuccess = (message: string) => {
    setSuccess(message);
    window.setTimeout(() => setSuccess(''), 4000);
  };

  // ── Profiles ────────────────────────────────────────────────────────────────

  const openCreateProfile = () =>
    setProfileModal({ id: 0, form: { ...emptyProfileForm, isDefault: profiles.length === 0 } });

  const openEditProfile = (p: PrinterProfile) =>
    setProfileModal({
      id: p.id,
      form: {
        name: p.name,
        printerName: p.printerName,
        printerType: p.printerType,
        paperWidthMm: p.paperWidthMm,
        isDefault: p.isDefault,
        kickCashDrawer: p.kickCashDrawer,
        isActive: p.isActive,
      },
    });

  const setForm = (patch: Partial<ProfileForm>) =>
    setProfileModal((m) => (m ? { ...m, form: { ...m.form, ...patch } } : m));

  const handleTypeChange = (type: PrinterType) =>
    setForm({ printerType: type, paperWidthMm: type === PrinterType.A4 ? 210 : 80 });

  const saveProfile = async () => {
    if (!profileModal) return;
    const { id, form } = profileModal;

    if (!form.name.trim() || !form.printerName.trim()) {
      setProfileError(t('printDevices.profileRequired') || 'Profile name and printer name are required.');
      return;
    }

    setProfileSaving(true);
    setProfileError('');
    try {
      if (id === 0) {
        await printerService.createProfile(form);
        flashSuccess(t('printDevices.profileCreated') || 'Printer profile created');
      } else {
        await printerService.updateProfile(id, form);
        flashSuccess(t('printDevices.profileUpdated') || 'Printer profile updated');
      }
      setProfileModal(null);
      await load();
    } catch (e: any) {
      setProfileError(
        e?.response?.data?.error?.message
        || t('printDevices.profileSaveFailed') || 'Failed to save printer profile');
    } finally {
      setProfileSaving(false);
    }
  };

  const deleteProfile = async (p: PrinterProfile) => {
    if (!window.confirm(t('printDevices.profileDeleteConfirm') || `Delete printer profile "${p.name}"?`)) return;
    try {
      await printerService.deleteProfile(p.id);
      flashSuccess(t('printDevices.profileDeleted') || 'Printer profile deleted');
      await load();
    } catch {
      setError(t('printDevices.profileDeleteFailed') || 'Failed to delete printer profile');
    }
  };

  // ── Agents ──────────────────────────────────────────────────────────────────

  const openRegisterAgent = () => {
    setAgentName('');
    setAgentError('');
    setIssuedKey(null);
    setKeyCopied(false);
    setAgentModalOpen(true);
  };

  const registerAgent = async () => {
    if (!agentName.trim()) {
      setAgentError(t('printDevices.agentNameRequired') || 'Agent name is required.');
      return;
    }
    setAgentSaving(true);
    setAgentError('');
    try {
      const res = await printerService.registerAgent(agentName.trim());
      if (res.data.data) setIssuedKey(res.data.data);
      await load();
    } catch (e: any) {
      setAgentError(
        e?.response?.data?.error?.message
        || t('printDevices.agentRegisterFailed') || 'Failed to register agent');
    } finally {
      setAgentSaving(false);
    }
  };

  const copyKey = async () => {
    if (!issuedKey) return;
    try {
      await navigator.clipboard.writeText(issuedKey.apiKey);
      setKeyCopied(true);
      window.setTimeout(() => setKeyCopied(false), 2500);
    } catch {
      /* clipboard unavailable — user can still select the text */
    }
  };

  const revokeAgent = async (agent: PrintAgent) => {
    if (!window.confirm(
      t('printDevices.agentRevokeConfirm')
      || `Revoke agent "${agent.name}"? Its API key stops working immediately.`)) return;
    try {
      await printerService.revokeAgent(agent.id);
      flashSuccess(t('printDevices.agentRevoked') || 'Agent revoked');
      await load();
    } catch {
      setError(t('printDevices.agentRevokeFailed') || 'Failed to revoke agent');
    }
  };

  // ── Print jobs ──────────────────────────────────────────────────────────────

  const retryJob = async (job: PrintJob) => {
    setRetryingJobId(job.id);
    setError('');
    try {
      await printerService.retryJob(job.id);
      flashSuccess(t('printDevices.jobRequeued') || 'Print job re-queued');
      await load();
    } catch (e: any) {
      setError(
        e?.response?.data?.error?.message
        || t('printDevices.jobRetryFailed') || 'Failed to retry print job');
    } finally {
      setRetryingJobId(null);
    }
  };

  const jobStatusBadge = (status: PrintJobStatus) => {
    switch (status) {
      case PrintJobStatus.Queued:
        return <span className="ims-badge ims-badge-pending">{t('printDevices.jobQueued') || 'Queued'}</span>;
      case PrintJobStatus.Dispatched:
        return <span className="ims-badge ims-badge-confirmed">{t('printDevices.jobDispatched') || 'Printing'}</span>;
      case PrintJobStatus.Completed:
        return <span className="ims-badge ims-badge-completed">{t('printDevices.jobCompleted') || 'Completed'}</span>;
      default:
        return <span className="ims-badge ims-badge-rejected">{t('printDevices.jobFailed') || 'Failed'}</span>;
    }
  };

  // ── Render helpers ──────────────────────────────────────────────────────────

  const typeLabel = (type: PrinterType) =>
    type === PrinterType.Thermal
      ? (t('printDevices.typeThermal') || 'Thermal (receipt)')
      : (t('printDevices.typeA4') || 'A4 (page)');

  const formatLastSeen = (iso: string | null) => {
    if (!iso) return t('printDevices.neverConnected') || 'Never';
    return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' })
      .format(new Date(iso + (iso.endsWith('Z') ? '' : 'Z')));
  };

  return (
    <Layout>
      <div className="ims-page print-devices-page">
        <div className="ims-list-header">
          <div>
            <h1 className="ims-list-title">{t('printDevices.title') || 'Printing Devices'}</h1>
            <p className="pd-subtitle">
              {t('printDevices.subtitle') || 'Printer profiles bills are routed to, and the site print agents that deliver them.'}
            </p>
          </div>
          <div className="ims-list-actions">
            <button className="btn-refresh" onClick={load} disabled={loading}>
              <RefreshCw size={15} /> {t('common.refresh') || 'Refresh'}
            </button>
          </div>
        </div>

        {error && <div className="ims-alert ims-alert-error">{error}</div>}
        {success && <div className="ims-alert ims-alert-success">{success}</div>}

        {/* ── Printer profiles ── */}
        <div className="ims-card pd-card">
          <div className="ims-card-header">
            <span className="ims-card-title">
              <Printer size={15} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
              {t('printDevices.profilesTitle') || 'Printer Profiles'}
            </span>
            <button className="ims-btn ims-btn-primary pd-add-btn" onClick={openCreateProfile}>
              <Plus size={14} /> {t('printDevices.addProfile') || 'Add Profile'}
            </button>
          </div>
          <div className="ims-card-body pd-table-body">
            {profiles.length === 0 && !loading ? (
              <div className="pd-empty">
                {t('printDevices.noProfiles')
                  || 'No printer profiles yet. Bills fall back to the default printer name in Bill Print Settings.'}
              </div>
            ) : (
              <table className="pd-table">
                <thead>
                  <tr>
                    <th>{t('printDevices.profileName') || 'Name'}</th>
                    <th>{t('printDevices.printerName') || 'Printer (Windows queue name)'}</th>
                    <th>{t('printDevices.printerType') || 'Type'}</th>
                    <th>{t('printDevices.paperWidth') || 'Paper'}</th>
                    <th>{t('common.status') || 'Status'}</th>
                    <th className="pd-actions-col" />
                  </tr>
                </thead>
                <tbody>
                  {profiles.map((p) => (
                    <tr key={p.id}>
                      <td>
                        {p.name}
                        {p.isDefault && (
                          <span className="ims-badge ims-badge-approved pd-default-badge">
                            {t('printDevices.default') || 'Default'}
                          </span>
                        )}
                      </td>
                      <td className="pd-mono">{p.printerName}</td>
                      <td>{typeLabel(p.printerType)}</td>
                      <td>{p.paperWidthMm} mm</td>
                      <td>
                        <span className={`ims-badge ${p.isActive ? 'ims-badge-completed' : 'ims-badge-closed'}`}>
                          {p.isActive ? (t('common.active') || 'Active') : (t('common.inactive') || 'Inactive')}
                        </span>
                      </td>
                      <td className="pd-row-actions">
                        <button className="grid-action-btn-edit" title={t('common.edit') || 'Edit'}
                          onClick={() => openEditProfile(p)}>
                          <SquarePen size={16} />
                        </button>
                        <button className="grid-action-btn-delete" title={t('common.delete') || 'Delete'}
                          onClick={() => deleteProfile(p)}>
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* ── Print agents ── */}
        <div className="ims-card pd-card">
          <div className="ims-card-header">
            <span className="ims-card-title">
              <Radio size={15} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
              {t('printDevices.agentsTitle') || 'Print Agents'}
            </span>
            <button className="ims-btn ims-btn-primary pd-add-btn" onClick={openRegisterAgent}>
              <Plus size={14} /> {t('printDevices.registerAgent') || 'Register Agent'}
            </button>
          </div>
          <div className="ims-card-body pd-table-body">
            {agents.length === 0 && !loading ? (
              <div className="pd-empty">
                {t('printDevices.noAgents')
                  || 'No print agents registered. Without an agent, printing only works when the server can reach the printer directly.'}
              </div>
            ) : (
              <table className="pd-table">
                <thead>
                  <tr>
                    <th>{t('printDevices.agentName') || 'Name'}</th>
                    <th>{t('common.status') || 'Status'}</th>
                    <th>{t('printDevices.lastSeen') || 'Last seen'}</th>
                    <th className="pd-actions-col" />
                  </tr>
                </thead>
                <tbody>
                  {agents.map((a) => (
                    <tr key={a.id}>
                      <td>{a.name}</td>
                      <td>
                        <span className={`ims-badge ${a.isActive ? 'ims-badge-completed' : 'ims-badge-closed'}`}>
                          {a.isActive ? (t('common.active') || 'Active') : (t('common.inactive') || 'Inactive')}
                        </span>
                      </td>
                      <td>{formatLastSeen(a.lastSeenAt)}</td>
                      <td className="pd-row-actions">
                        <button className="grid-action-btn-delete" title={t('printDevices.revoke') || 'Revoke'}
                          onClick={() => revokeAgent(a)}>
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* ── Recent print jobs ── */}
        <div className="ims-card pd-card">
          <div className="ims-card-header">
            <span className="ims-card-title">
              <ListChecks size={15} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
              {t('printDevices.jobsTitle') || 'Recent Print Jobs'}
            </span>
          </div>
          <div className="ims-card-body pd-table-body">
            {jobs.length === 0 && !loading ? (
              <div className="pd-empty">
                {t('printDevices.noJobs') || 'No print jobs yet.'}
              </div>
            ) : (
              <table className="pd-table">
                <thead>
                  <tr>
                    <th>{t('printDevices.jobDocument') || 'Document'}</th>
                    <th>{t('printDevices.printerName') || 'Printer'}</th>
                    <th>{t('common.status') || 'Status'}</th>
                    <th>{t('printDevices.jobAttempts') || 'Attempts'}</th>
                    <th>{t('printDevices.jobError') || 'Error'}</th>
                    <th>{t('printDevices.jobCreated') || 'Created'}</th>
                    <th className="pd-actions-col" />
                  </tr>
                </thead>
                <tbody>
                  {jobs.map((j) => (
                    <tr key={j.id}>
                      <td>{j.documentReference}</td>
                      <td className="pd-mono">{j.printerName}</td>
                      <td>{jobStatusBadge(j.status)}</td>
                      <td>{j.attempts}</td>
                      <td className="pd-job-error" title={j.errorMessage ?? ''}>
                        {j.errorMessage ?? '—'}
                      </td>
                      <td>{formatLastSeen(j.createdAt)}</td>
                      <td className="pd-row-actions">
                        {(j.status === PrintJobStatus.Failed || j.status === PrintJobStatus.Dispatched) && (
                          <button
                            className="grid-action-btn-view"
                            title={t('printDevices.retry') || 'Retry'}
                            disabled={retryingJobId === j.id}
                            onClick={() => retryJob(j)}
                          >
                            <RotateCcw size={16} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* ── Profile modal ── */}
        {profileModal && (
          <div className="pd-modal-overlay" onClick={() => !profileSaving && setProfileModal(null)}>
            <div className="pd-modal" onClick={(e) => e.stopPropagation()}>
              <h2 className="pd-modal-title">
                {profileModal.id === 0
                  ? (t('printDevices.addProfile') || 'Add Profile')
                  : (t('printDevices.editProfile') || 'Edit Profile')}
              </h2>

              {profileError && <div className="ims-alert ims-alert-error">{profileError}</div>}

              <label className="pd-field">
                <span>{t('printDevices.profileName') || 'Name'}</span>
                <input className="ims-form-control" value={profileModal.form.name}
                  placeholder={t('printDevices.profileNamePlaceholder') || 'e.g. Counter 1 receipt'}
                  onChange={(e) => setForm({ name: e.target.value })} />
              </label>

              <label className="pd-field">
                <span>
                  {t('printDevices.printerName') || 'Printer (Windows queue name)'}
                  <FieldHint text={t('printDevices.printerNameHint')
                    || 'Must exactly match the printer name on the PC running the print agent. Find it in Windows under Settings → Printers, or run Get-Printer in PowerShell.'} />
                </span>
                <input className="ims-form-control" value={profileModal.form.printerName}
                  placeholder={t('printDevices.printerNamePlaceholder') || 'Exact name from Get-Printer on the agent PC'}
                  onChange={(e) => setForm({ printerName: e.target.value })} />
              </label>

              <div className="pd-field-row">
                <label className="pd-field">
                  <span>{t('printDevices.printerType') || 'Type'}</span>
                  <select className="ims-form-control" value={profileModal.form.printerType}
                    onChange={(e) => handleTypeChange(Number(e.target.value) as PrinterType)}>
                    <option value={PrinterType.Thermal}>{typeLabel(PrinterType.Thermal)}</option>
                    <option value={PrinterType.A4}>{typeLabel(PrinterType.A4)}</option>
                  </select>
                </label>

                <label className="pd-field">
                  <span>{t('printDevices.paperWidth') || 'Paper'}</span>
                  {profileModal.form.printerType === PrinterType.Thermal ? (
                    <select className="ims-form-control" value={profileModal.form.paperWidthMm}
                      onChange={(e) => setForm({ paperWidthMm: Number(e.target.value) })}>
                      <option value={80}>80 mm</option>
                      <option value={58}>58 mm</option>
                    </select>
                  ) : (
                    <input className="ims-form-control" value="210 mm (A4)" disabled />
                  )}
                </label>
              </div>

              <label className="pd-check">
                <input type="checkbox" checked={profileModal.form.isDefault}
                  onChange={(e) => setForm({ isDefault: e.target.checked })} />
                <span>{t('printDevices.setDefault') || 'Use as the default bill printer'}</span>
              </label>

              {profileModal.form.printerType === PrinterType.Thermal && (
                <label className="pd-check">
                  <input type="checkbox" checked={profileModal.form.kickCashDrawer}
                    onChange={(e) => setForm({ kickCashDrawer: e.target.checked })} />
                  <span>
                    {t('printDevices.kickCashDrawer') || 'Open the cash drawer when printing (drawer wired to printer)'}
                  </span>
                </label>
              )}

              {profileModal.id !== 0 && (
                <label className="pd-check">
                  <input type="checkbox" checked={profileModal.form.isActive}
                    onChange={(e) => setForm({ isActive: e.target.checked })} />
                  <span>{t('common.active') || 'Active'}</span>
                </label>
              )}

              <div className="pd-modal-actions">
                <button className="ims-btn ims-btn-secondary" disabled={profileSaving}
                  onClick={() => setProfileModal(null)}>
                  {t('common.cancel') || 'Cancel'}
                </button>
                <button className="ims-btn ims-btn-primary" disabled={profileSaving} onClick={saveProfile}>
                  {profileSaving ? (t('common.saving') || 'Saving…') : (t('common.save') || 'Save')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Agent registration modal ── */}
        {agentModalOpen && (
          <div className="pd-modal-overlay" onClick={() => !agentSaving && setAgentModalOpen(false)}>
            <div className="pd-modal" onClick={(e) => e.stopPropagation()}>
              <h2 className="pd-modal-title">
                {issuedKey
                  ? (t('printDevices.agentKeyTitle') || 'Agent API Key')
                  : (t('printDevices.registerAgent') || 'Register Agent')}
              </h2>

              {agentError && <div className="ims-alert ims-alert-error">{agentError}</div>}

              {!issuedKey ? (
                <>
                  <label className="pd-field">
                    <span>
                      {t('printDevices.agentName') || 'Name'}
                      <FieldHint text={t('printDevices.agentNameHint')
                        || 'A label for the PC where the IMS Print Agent runs — e.g. the till computer. One agent per site is enough if all printers are reachable from it.'} />
                    </span>
                    <input className="ims-form-control" value={agentName}
                      placeholder={t('printDevices.agentNamePlaceholder') || 'e.g. Main counter PC'}
                      onChange={(e) => setAgentName(e.target.value)} />
                  </label>

                  <div className="pd-modal-actions">
                    <button className="ims-btn ims-btn-secondary" disabled={agentSaving}
                      onClick={() => setAgentModalOpen(false)}>
                      {t('common.cancel') || 'Cancel'}
                    </button>
                    <button className="ims-btn ims-btn-primary" disabled={agentSaving} onClick={registerAgent}>
                      {agentSaving
                        ? (t('common.saving') || 'Saving…')
                        : (t('printDevices.register') || 'Register')}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="ims-alert ims-alert-warning">
                    {t('printDevices.agentKeyWarning')
                      || 'Copy this key now — it is shown only once and cannot be retrieved later.'}
                  </div>

                  <div className="pd-key-box">
                    <KeyRound size={16} className="pd-key-icon" />
                    <code className="pd-key-value">{issuedKey.apiKey}</code>
                    <button className="ims-btn ims-btn-secondary pd-copy-btn" onClick={copyKey}>
                      {keyCopied ? <Check size={14} /> : <Copy size={14} />}
                      {keyCopied ? (t('printDevices.copied') || 'Copied') : (t('common.copy') || 'Copy')}
                    </button>
                  </div>

                  <p className="pd-key-hint">
                    {t('printDevices.agentKeyHint')
                      || 'Paste it as Agent:ApiKey in the IMS Print Agent appsettings.json on the site PC, then start the agent.'}
                  </p>

                  <div className="pd-modal-actions">
                    <button className="ims-btn ims-btn-primary" onClick={() => setAgentModalOpen(false)}>
                      {t('common.done') || 'Done'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PrintDevicesPage;
